import type { ExportFormatType } from '../../../redux/types/data-options'
import { emptySplitApi as api } from './emptyApi'

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    updateTrainingMatrix: build.mutation<UpdateTrainingMatrixApiResponse, UpdateTrainingMatrixApiArg>({
      query: queryArg => ({
        url: `/views/training-matrix/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.trainingMatrixViewDto
      })
    }),
    deleteTrainingMatrix: build.mutation<DeleteTrainingMatrixApiResponse, DeleteTrainingMatrixApiArg>({
      query: queryArg => ({
        url: `/views/training-matrix/${queryArg.id}`,
        method: 'DELETE'
      })
    }),
    updateGapAnalysisView: build.mutation<UpdateGapAnalysisViewApiResponse, UpdateGapAnalysisViewApiArg>({
      query: queryArg => ({
        url: `/views/gap-analysis/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.gapAnalysisViewDto
      })
    }),
    deleteGapAnalysis: build.mutation<DeleteGapAnalysisApiResponse, DeleteGapAnalysisApiArg>({
      query: queryArg => ({
        url: `/views/gap-analysis/${queryArg.id}`,
        method: 'DELETE'
      })
    }),
    trainingMatrixReport: build.query<TrainingMatrixReportApiResponse, TrainingMatrixReportApiArg>({
      query: queryArg => ({ url: `/schedules/${queryArg.id}` })
    }),
    updateTrainingMatrixReport: build.mutation<UpdateTrainingMatrixReportApiResponse, UpdateTrainingMatrixReportApiArg>(
      {
        query: queryArg => ({
          url: `/schedules/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.scheduledTrainingReportDto
        })
      }
    ),
    deleteTrainingMatrixReport: build.mutation<DeleteTrainingMatrixReportApiResponse, DeleteTrainingMatrixReportApiArg>(
      {
        query: queryArg => ({
          url: `/schedules/${queryArg.id}`,
          method: 'DELETE'
        })
      }
    ),
    getTrainingMatrixViews: build.query<GetTrainingMatrixViewsApiResponse, GetTrainingMatrixViewsApiArg>({
      query: queryArg => ({
        url: `/views/training-matrix`,
        params: { page: queryArg.page, filter: queryArg.filter }
      })
    }),
    createTrainingMatrixView: build.mutation<CreateTrainingMatrixViewApiResponse, CreateTrainingMatrixViewApiArg>({
      query: queryArg => ({
        url: `/views/training-matrix`,
        method: 'POST',
        body: queryArg.trainingMatrixViewDto
      })
    }),
    getGapAnalysisViews: build.query<GetGapAnalysisViewsApiResponse, GetGapAnalysisViewsApiArg>({
      query: queryArg => ({
        url: `/views/gap-analysis`,
        params: { page: queryArg.page, filter: queryArg.filter }
      })
    }),
    createGapAnalysisView: build.mutation<CreateGapAnalysisViewApiResponse, CreateGapAnalysisViewApiArg>({
      query: queryArg => ({
        url: `/views/gap-analysis`,
        method: 'POST',
        body: queryArg.gapAnalysisViewDto
      })
    }),
    trainingMatrixLayout: build.mutation<TrainingMatrixLayoutApiResponse, TrainingMatrixLayoutApiArg>({
      query: queryArg => ({
        url: `/training-matrix/dynamic`,
        method: 'POST',
        body: queryArg.trainingMatrixFilters
      })
    }),
    trainingMatrixReports: build.query<TrainingMatrixReportsApiResponse, TrainingMatrixReportsApiArg>({
      query: queryArg => ({
        url: `/schedules`,
        params: { page: queryArg.page }
      })
    }),
    createTrainingMatrixReport: build.mutation<CreateTrainingMatrixReportApiResponse, CreateTrainingMatrixReportApiArg>(
      {
        query: queryArg => ({
          url: `/schedules`,
          method: 'POST',
          body: queryArg.scheduledTrainingReportDto
        })
      }
    ),
    gapAnalysisLayout: build.mutation<GapAnalysisLayoutApiResponse, GapAnalysisLayoutApiArg>({
      query: queryArg => ({
        url: `/gap-analysis/dynamic`,
        method: 'POST',
        body: queryArg.gapAnalysisFilters
      })
    }),
    trainingMatrixViewPage: build.query<TrainingMatrixViewPageApiResponse, TrainingMatrixViewPageApiArg>({
      query: queryArg => ({
        url: `/training-matrix/view/${queryArg.viewId}`,
        params: { page: queryArg.page }
      })
    }),
    trainingMatrixViewLayout: build.query<TrainingMatrixViewLayoutApiResponse, TrainingMatrixViewLayoutApiArg>({
      query: queryArg => ({
        url: `/training-matrix/view/${queryArg.viewId}/layout`
      })
    }),
    trainingMatrixViewExcel: build.query<TrainingMatrixViewExcelApiResponse, TrainingMatrixViewExcelApiArg>({
      query: queryArg => ({
        url: `/training-matrix/view/${queryArg.viewId}/export`,
        params: {
          style: queryArg.style,
          showGroups: queryArg.showGroups,
          showManagers: queryArg.showManagers,
          useColorBlindPalette: queryArg.useColorBlindPalette,
          hierarchies: queryArg.hierarchies,
          showOverallPercentageOfCompliance: queryArg.showOverallPercentageOfCompliance,
          showPercentageOfComplianceForIndividuals: queryArg.showPercentageOfComplianceForIndividuals,
          showPercentageOfComplianceForCompetencies: queryArg.showPercentageOfComplianceForCompetencies,
          showPercentageOfComplianceForCompetencyGroups: queryArg.showPercentageOfComplianceForCompetencyGroups
        }
      })
    }),
    trainingMatrixPage: build.query<TrainingMatrixPageApiResponse, TrainingMatrixPageApiArg>({
      query: queryArg => ({
        url: `/training-matrix/dynamic/${queryArg.reportHash}`,
        params: { page: queryArg.page }
      })
    }),
    reportStatistics: build.query<ReportStatisticsApiResponse, ReportStatisticsApiArg>({
      query: queryArg => ({
        url: `/training-matrix/dynamic/${queryArg.reportHash}/statistics`
      })
    }),
    trainingMatrixExcel: build.query<TrainingMatrixExcelApiResponse, TrainingMatrixExcelApiArg>({
      query: queryArg => ({
        url: `/training-matrix/dynamic/${queryArg.reportHash}/export`,
        params: {
          style: queryArg.style,
          showGroups: queryArg.showGroups,
          showManagers: queryArg.showManagers,
          useColorBlindPalette: queryArg.useColorBlindPalette,
          hierarchies: queryArg.hierarchies,
          showOverallPercentageOfCompliance: queryArg.showOverallPercentageOfCompliance,
          showPercentageOfComplianceForIndividuals: queryArg.showPercentageOfComplianceForIndividuals,
          showPercentageOfComplianceForCompetencies: queryArg.showPercentageOfComplianceForCompetencies,
          showPercentageOfComplianceForCompetencyGroups: queryArg.showPercentageOfComplianceForCompetencyGroups
        }
      })
    }),
    getSchedulesExportByReportId: build.query<
      GetSchedulesExportByReportIdApiResponse,
      GetSchedulesExportByReportIdApiArg
    >({
      query: queryArg => ({ url: `/schedules/export/${queryArg.reportId}` })
    }),
    gapAnalysisViewPage: build.query<GapAnalysisViewPageApiResponse, GapAnalysisViewPageApiArg>({
      query: queryArg => ({
        url: `/gap-analysis/view/${queryArg.viewId}`,
        params: { page: queryArg.page }
      })
    }),
    gapAnalysisViewLayout: build.query<GapAnalysisViewLayoutApiResponse, GapAnalysisViewLayoutApiArg>({
      query: queryArg => ({
        url: `/gap-analysis/view/${queryArg.viewId}/layout`
      })
    }),
    gapAnalysisViewExcel: build.query<GapAnalysisViewExcelApiResponse, GapAnalysisViewExcelApiArg>({
      query: queryArg => ({
        url: `/gap-analysis/view/${queryArg.viewId}/export`
      })
    }),
    gapAnalysisPage: build.query<GapAnalysisPageApiResponse, GapAnalysisPageApiArg>({
      query: queryArg => ({
        url: `/gap-analysis/dynamic/${queryArg.reportHash}`,
        params: { page: queryArg.page }
      })
    }),
    gapAnalysisExcel: build.query<GapAnalysisExcelApiResponse, GapAnalysisExcelApiArg>({
      query: queryArg => ({
        url: `/gap-analysis/dynamic/${queryArg.reportHash}/export`,
        params: { visibleColumns: queryArg.visibleColumns }
      })
    }),
    people: build.query<PeopleApiResponse, PeopleApiArg>({
      query: queryArg => ({
        url: `/filters/people`,
        params: { filter: queryArg.filter, page: queryArg.page }
      })
    }),
    occupations: build.query<OccupationsApiResponse, OccupationsApiArg>({
      query: queryArg => ({
        url: `/filters/occupations`,
        params: { filter: queryArg.filter, page: queryArg.page }
      })
    }),
    managers: build.query<ManagersApiResponse, ManagersApiArg>({
      query: queryArg => ({
        url: `/filters/managers`,
        params: { filter: queryArg.filter, page: queryArg.page }
      })
    }),
    hierarchyValues: build.query<HierarchyValuesApiResponse, HierarchyValuesApiArg>({
      query: () => ({ url: `/filters/hierarchy-values` })
    }),
    hierarchies: build.query<HierarchiesApiResponse, HierarchiesApiArg>({
      query: () => ({ url: `/filters/hierarchies` })
    }),
    competencies: build.query<CompetenciesApiResponse, CompetenciesApiArg>({
      query: queryArg => ({
        url: `/filters/competencies`,
        params: { filter: queryArg.filter, page: queryArg.page }
      })
    })
  }),
  overrideExisting: false
})
export { injectedRtkApi as tmsApi }
export type UpdateTrainingMatrixApiResponse = /** status 200 OK */ TrainingMatrixViewDto
export type UpdateTrainingMatrixApiArg = {
  id: string
  trainingMatrixViewDto: TrainingMatrixViewDto
}
export type DeleteTrainingMatrixApiResponse = unknown
export type DeleteTrainingMatrixApiArg = {
  id: string
}
export type UpdateGapAnalysisViewApiResponse = /** status 200 OK */ GapAnalysisViewDto
export type UpdateGapAnalysisViewApiArg = {
  id: string
  gapAnalysisViewDto: GapAnalysisViewDto
}
export type DeleteGapAnalysisApiResponse = unknown
export type DeleteGapAnalysisApiArg = {
  id: string
}
export type TrainingMatrixReportApiResponse = /** status 200 OK */ ScheduledTrainingReportDto
export type TrainingMatrixReportApiArg = {
  id: string
}
export type UpdateTrainingMatrixReportApiResponse = /** status 200 OK */ ScheduledTrainingReportDto
export type UpdateTrainingMatrixReportApiArg = {
  id: string
  scheduledTrainingReportDto: ScheduledTrainingReportDto
}
export type DeleteTrainingMatrixReportApiResponse = unknown
export type DeleteTrainingMatrixReportApiArg = {
  id: string
}
export type GetTrainingMatrixViewsApiResponse = /** status 200 OK */ ResponseDtoTrainingMatrixViewDto
export type GetTrainingMatrixViewsApiArg = {
  page?: number
  filter?: string
}
export type CreateTrainingMatrixViewApiResponse = /** status 200 OK */ TrainingMatrixViewDto
export type CreateTrainingMatrixViewApiArg = {
  trainingMatrixViewDto: TrainingMatrixViewDto
}
export type GetGapAnalysisViewsApiResponse = /** status 200 OK */ ResponseDtoGapAnalysisViewDto
export type GetGapAnalysisViewsApiArg = {
  page?: number
  filter?: string
}
export type CreateGapAnalysisViewApiResponse = /** status 200 OK */ GapAnalysisViewDto
export type CreateGapAnalysisViewApiArg = {
  gapAnalysisViewDto: GapAnalysisViewDto
}
export type TrainingMatrixLayoutApiResponse = /** status 200 OK */ TrainingReportLayout
export type TrainingMatrixLayoutApiArg = {
  trainingMatrixFilters: TrainingMatrixFilters
}
export type TrainingMatrixReportsApiResponse = /** status 200 OK */ ResponseDtoScheduledTrainingReportDto
export type TrainingMatrixReportsApiArg = {
  page?: number
}
export type CreateTrainingMatrixReportApiResponse = /** status 200 OK */ ResultDtoScheduledTrainingReportDto
export type CreateTrainingMatrixReportApiArg = {
  scheduledTrainingReportDto: ScheduledTrainingReportDto
}
export type GapAnalysisLayoutApiResponse = /** status 200 OK */ TrainingReportLayout
export type GapAnalysisLayoutApiArg = {
  gapAnalysisFilters: GapAnalysisFilters
}
export type TrainingMatrixViewPageApiResponse = /** status 200 OK */ TrainingMatrixPart
export type TrainingMatrixViewPageApiArg = {
  viewId: string
  page?: number
}
export type TrainingMatrixViewLayoutApiResponse = /** status 200 OK */ TrainingReportLayout
export type TrainingMatrixViewLayoutApiArg = {
  viewId: string
}
export type TrainingMatrixViewExcelApiResponse = /** status 200 OK */ Blob
export type TrainingMatrixViewExcelApiArg = {
  viewId: string
  style?: ExportFormatType
  showGroups?: boolean
  showManagers?: boolean
  useColorBlindPalette?: boolean
  hierarchies?: string[]
  showOverallPercentageOfCompliance?: boolean
  showPercentageOfComplianceForIndividuals?: boolean
  showPercentageOfComplianceForCompetencies?: boolean
  showPercentageOfComplianceForCompetencyGroups?: boolean
}
export type TrainingMatrixPageApiResponse = /** status 200 OK */ TrainingMatrixPart
export type TrainingMatrixPageApiArg = {
  reportHash: string
  page?: number
}
export type ReportStatisticsApiResponse = /** status 200 OK */ ComplianceStatisticsDto
export type ReportStatisticsApiArg = {
  reportHash: string
}
export type TrainingMatrixExcelApiResponse = /** status 200 OK */ Blob
export type TrainingMatrixExcelApiArg = {
  reportHash: string
  style?: ExportFormatType
  showGroups?: boolean
  showManagers?: boolean
  useColorBlindPalette?: boolean
  hierarchies?: string[]
  showOverallPercentageOfCompliance?: boolean
  showPercentageOfComplianceForIndividuals?: boolean
  showPercentageOfComplianceForCompetencies?: boolean
  showPercentageOfComplianceForCompetencyGroups?: boolean
}
export type GetSchedulesExportByReportIdApiResponse = /** status 200 OK */ Blob
export type GetSchedulesExportByReportIdApiArg = {
  reportId: string
}
export type GapAnalysisViewPageApiResponse = /** status 200 OK */ GapAnalysisPart
export type GapAnalysisViewPageApiArg = {
  viewId: string
  page?: number
}
export type GapAnalysisViewLayoutApiResponse = /** status 200 OK */ TrainingReportLayout
export type GapAnalysisViewLayoutApiArg = {
  viewId: string
}
export type GapAnalysisViewExcelApiResponse = /** status 200 OK */ Blob
export type GapAnalysisViewExcelApiArg = {
  viewId: string
}
export type GapAnalysisPageApiResponse = /** status 200 OK */ GapAnalysisPart
export type GapAnalysisPageApiArg = {
  reportHash: string
  page?: number
}
export type GapAnalysisExcelApiResponse = /** status 200 OK */ Blob
export type GapAnalysisExcelApiArg = {
  reportHash: string
  visibleColumns?: string[]
}
export type PeopleApiResponse = /** status 200 OK */ ResponseDtoUser
export type PeopleApiArg = {
  filter?: string
  page?: number
}
export type OccupationsApiResponse = /** status 200 OK */ ResponseDtoOccupation
export type OccupationsApiArg = {
  filter?: string
  page?: number
}
export type ManagersApiResponse = /** status 200 OK */ ResponseDtoUser
export type ManagersApiArg = {
  filter?: string
  page?: number
}
export type HierarchyValuesApiResponse = /** status 200 OK */ HierarchyType[]
export type HierarchyValuesApiArg = void
export type HierarchiesApiResponse = /** status 200 OK */ HierarchyType[]
export type HierarchiesApiArg = void
export type CompetenciesApiResponse = /** status 200 OK */ ResponseDtoCompetency
export type CompetenciesApiArg = {
  filter?: string
  page?: number
}
export type User = {
  id?: string
  fullName?: string
  occupation?: string
  affiliation?: string
  secondaryOccupations?: string[]
  hierarchies?: {
    [key: string]: string
  }
  runtimeMetadata?: {
    [key: string]: string
  }
  manager?: User
}
export type ViewDisplayMetadataDto = {
  managers?: User[]
}
export type ViewSettingsDto = {
  angleCompetencyHeaders?: boolean
  expirationDateLimit?: number
  gridlineFrequency?: number
  showCompetencyGroups?: boolean
  useColorBlindPalette?: boolean
  showDates?: boolean
  showGridlines?: boolean
  showDesiredCompetencies?: boolean
  showOverallCompliancePercentage?: boolean
  showPercentageOfComplianceForIndividuals?: boolean
  showPercentageOfComplianceForCompetencies?: boolean
  showPercentageOfComplianceForCompetencyGroups?: boolean
  isManagerColumnSelected?: boolean
  selectedColumns?: string[]
  exportFormat?: ExportFormatType
}
export type Competency = {
  id?: string
  name?: string
}
export type HierarchyCriteria = {
  hierarchyKey?: string
  values?: string[]
}
export type TrainingMatrixFilters = {
  managerIds?: string[]
  showAllReports?: boolean
  showDesiredCompetencies?: boolean
  applyHierarchyFiltersToOccupationRequirements?: boolean
  people?: User[]
  competencies?: Competency[]
  matchOption?: 'MATCH_ANY' | 'MATCH_ALL'
  hierarchies?: HierarchyCriteria[]
  occupations?: string[]
  ignoreHierarchyFilterOccupationRequirements?: boolean
  showAllCompetencies?: boolean
  aboutToExpireThreshold?: number
}
export type TrainingMatrixViewDto = {
  id?: string
  title?: string
  global?: boolean
  displayMetadata?: ViewDisplayMetadataDto
  settings?: ViewSettingsDto
  filters?: TrainingMatrixFilters
}
export type GapAnalysisFilters = {
  managerIds?: string[]
  showAllReports?: boolean
  showDesiredCompetencies?: boolean
  applyHierarchyFiltersToOccupationRequirements?: boolean
  people?: User[]
  competencies?: Competency[]
  matchOption?: 'MATCH_ANY' | 'MATCH_ALL'
  hierarchies?: HierarchyCriteria[]
  occupations?: string[]
  reportDisplaySettings?: string[]
}
export type GapAnalysisViewDto = {
  id?: string
  title?: string
  global?: boolean
  displayMetadata?: ViewDisplayMetadataDto
  settings?: ViewSettingsDto
  filters?: GapAnalysisFilters
}
export type ScheduledTrainingReportRuleDto = {
  interval: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
  hourOfDay: number
  dayOfMonth?: number
  dayOfWeek?: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'
  month?:
    | 'JANUARY'
    | 'FEBRUARY'
    | 'MARCH'
    | 'APRIL'
    | 'MAY'
    | 'JUNE'
    | 'JULY'
    | 'AUGUST'
    | 'SEPTEMBER'
    | 'OCTOBER'
    | 'NOVEMBER'
    | 'DECEMBER'
}
export type ScheduledTrainingReportDto = {
  id?: string
  name: string
  subject: string
  timeZone: string
  viewId: string
  rules?: ScheduledTrainingReportRuleDto[]
  recipients?: string[]
}
export type ResponseDtoTrainingMatrixViewDto = {
  page?: number
  maxPages?: number
  total?: number
  data?: TrainingMatrixViewDto[]
}
export type ResponseDtoGapAnalysisViewDto = {
  page?: number
  maxPages?: number
  total?: number
  data?: GapAnalysisViewDto[]
}
export type CompetencyGroup = {
  id?: string
  name?: string
  ungrouped?: boolean
  competencies?: string[]
}
export type TrainingReportLayout = {
  hashKey?: string
  totalUsers?: number
  totalPages?: number
  hasInTrainingStatus?: boolean
  requiredCompetencies?: Competency[]
  desiredCompetencies?: Competency[]
  completedCompetencies?: Competency[]
  competencyGroups?: CompetencyGroup[]
}
export type ResponseDtoScheduledTrainingReportDto = {
  page?: number
  maxPages?: number
  total?: number
  data?: ScheduledTrainingReportDto[]
}
export type PayloadValidationStatus = {
  errors?: string[]
  warnings?: string[]
}
export type ResultDtoScheduledTrainingReportDto = {
  validationStatus?: PayloadValidationStatus
  result?: ScheduledTrainingReportDto
}
export type TrainingData = {
  type?: 'MANDATORY' | 'DESIRED' | 'IRRELEVANT'
  status?: 'IN_TRAINING' | 'COMPLETED' | 'NOT_COMPLETED' | 'EXPIRED' | 'ABOUT_TO_EXPIRE'
  startedOn?: string
  completedOn?: string
  expireOn?: string
  scheduleOn?: string
}
export type TrainingMatrixData = {
  status?: {
    [key: string]: TrainingData
  }
}
export type TrainingMatrixPart = {
  page?: number
  maxPages?: number
  total?: number
  trainingData?: {
    [key: string]: TrainingMatrixData
  }
  users?: User[]
}
export type ComplianceDto = {
  requiredCompliance?: number
  desiredCompliance?: number
}
export type ComplianceStatisticsDto = {
  reportHash?: string
  groupCompliance?: {
    [key: string]: ComplianceDto
  }
  competencyCompliance?: {
    [key: string]: ComplianceDto
  }
  userCompliance?: {
    [key: string]: ComplianceDto
  }
  overallCompliance?: ComplianceDto
}
export type GapAnalysisGroupDto = {
  necessary?: string[]
  completed?: string[]
  uncompleted?: string[]
  inTraining?: string[]
}
export type GapAnalysisData = {
  required?: GapAnalysisGroupDto
  desired?: GapAnalysisGroupDto
}
export type GapAnalysisPart = {
  page?: number
  maxPages?: number
  total?: number
  trainingData?: {
    [key: string]: GapAnalysisData
  }
  users?: User[]
}
export type ResponseDtoUser = {
  page?: number
  maxPages?: number
  total?: number
  data?: User[]
}
export type Occupation = {
  id?: string
  name?: string
}
export type ResponseDtoOccupation = {
  page?: number
  maxPages?: number
  total?: number
  data?: Occupation[]
}
export type HierarchyType = {
  id?: string
  name?: string
  weight?: number
  values?: string[]
}
export type ResponseDtoCompetency = {
  page?: number
  maxPages?: number
  total?: number
  data?: Competency[]
}
export const {
  useUpdateTrainingMatrixMutation,
  useDeleteTrainingMatrixMutation,
  useUpdateGapAnalysisViewMutation,
  useDeleteGapAnalysisMutation,
  useTrainingMatrixReportQuery,
  useUpdateTrainingMatrixReportMutation,
  useDeleteTrainingMatrixReportMutation,
  useGetTrainingMatrixViewsQuery,
  useCreateTrainingMatrixViewMutation,
  useGetGapAnalysisViewsQuery,
  useCreateGapAnalysisViewMutation,
  useTrainingMatrixLayoutMutation,
  useTrainingMatrixReportsQuery,
  useCreateTrainingMatrixReportMutation,
  useGapAnalysisLayoutMutation,
  useTrainingMatrixViewPageQuery,
  useTrainingMatrixViewLayoutQuery,
  useTrainingMatrixViewExcelQuery,
  useTrainingMatrixPageQuery,
  useReportStatisticsQuery,
  useTrainingMatrixExcelQuery,
  useGetSchedulesExportByReportIdQuery,
  useGapAnalysisViewPageQuery,
  useGapAnalysisViewLayoutQuery,
  useGapAnalysisViewExcelQuery,
  useGapAnalysisPageQuery,
  useGapAnalysisExcelQuery,
  usePeopleQuery,
  useOccupationsQuery,
  useManagersQuery,
  useHierarchyValuesQuery,
  useHierarchiesQuery,
  useCompetenciesQuery
} = injectedRtkApi
